import { useState, useEffect, useCallback } from 'react';

/**
 * useNetworkStatus - Hook for monitoring network connectivity
 * Returns current online status and pending sync count
 */
export const useNetworkStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [lastSyncResult, setLastSyncResult] = useState(null);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      console.log('[Network] Back online');
    };
    
    const handleOffline = () => {
      setIsOnline(false);
      console.log('[Network] Gone offline');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Listen for service worker messages
    const handleMessage = (event) => {
      if (event.data?.type === 'REQUEST_QUEUED') {
        setPendingCount(prev => prev + 1);
      } else if (event.data?.type === 'REQUEST_SYNCED') {
        setPendingCount(prev => Math.max(0, prev - 1));
      } else if (event.data?.type === 'SYNC_COMPLETE') {
        setLastSyncResult({
          synced: event.data.synced,
          failed: event.data.failed,
          remaining: event.data.remaining,
          timestamp: Date.now()
        });
        setPendingCount(event.data.remaining);
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleMessage);
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleMessage);
      }
    };
  }, []);

  // Manually trigger sync
  const triggerSync = useCallback(async () => {
    if ('serviceWorker' in navigator && 'sync' in window.registration) {
      try {
        const registration = await navigator.serviceWorker.ready;
        await registration.sync.register('sync-pending-requests');
        console.log('[Network] Manual sync triggered');
      } catch (error) {
        console.error('[Network] Failed to trigger sync:', error);
      }
    }
  }, []);

  return {
    isOnline,
    isOffline: !isOnline,
    pendingCount,
    hasPendingRequests: pendingCount > 0,
    lastSyncResult,
    triggerSync
  };
};

export default useNetworkStatus;
